import { ShopifySectionRenderingSchema } from '@/scripts/types/theme'
import { SectionApiResponse } from '@/scripts/types/responses'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { ATTRIBUTES, SELECTORS } from '@/scripts/core/global'

export class CartDrawer extends UcoastEl {
	static htmlSelector = 'cart-drawer'
	static selectors = {
		overlay: '[data-uc-cart-drawer-overlay]',
		innerEmpty: '[data-uc-cart-drawer-inner-empty]',
		inner: '[data-uc-cart-drawer-inner]',
		container: '#CartDrawer',
		cartLink: '#CartIconBubble',
		closeButton: '[data-uc-cart-drawer-close-button]',
		noteSummary: '[data-uc-cart-note-summary]',
		noteDetails: '[data-uc-cart-note-details]',
	}
	activeElement?: HTMLElement
	productId?: string
	constructor() {
		super()

		this.addEventListener('keyup', (evt: KeyboardEvent) => evt.code === 'Escape' && this.close());
		this.getOverlay().addEventListener('click', this.close.bind(this));
		this.setHeaderCartIconAccessibility();
	}

	getOverlay() {
		return q.rs(CartDrawer.selectors.overlay, this)
	}

	setHeaderCartIconAccessibility() {
		const cartLinks = q.rl(CartDrawer.selectors.cartLink)
		cartLinks.forEach((cartLink) => {
			cartLink.setAttribute('role', 'button')
			cartLink.setAttribute('aria-haspopup', 'dialog')
			cartLink.addEventListener('click', (event) => {
				event.preventDefault()
				this.open(cartLink)
			})
			cartLink.addEventListener('keydown', (event) => {
				if (event.code.toUpperCase() === 'SPACE') {
					event.preventDefault()
					this.open(cartLink)
				}
			})

			if(this.hasCartHash()) {
				cartLink.click();
			}
		})
	}

	hasCartHash() {
		return window.location.hash.includes('#cart')
	}

	clearCartHash() {
		if(window.location.hash.includes('#cart')) {
			history.pushState({}, '', window.location.origin);
		}
	}

	async refresh() {
		const sectionIds = this.getSectionsToRender()
			.filter((section) => section.id)
			.map((section) => section.id)
		const updatedHTML = await fetch(`/?sections=${sectionIds}`)
			.then((response) => response.json())
			.catch((e) => console.error(e))
		const formattedResponse: SectionApiResponse = {
			id: 'refresh',
			key: (new Date()).toDateString(),
			sections: updatedHTML
		}
		this.renderContents(formattedResponse)
	}

	open(triggeredBy?: HTMLElement) {
		if (triggeredBy) this.setActiveElement(triggeredBy)
		const cartDrawerNote = q.os(CartDrawer.selectors.noteSummary, this)
		if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote)
		// here the animation doesn't seem to always get triggered. A timeout seem to help
		setTimeout(() => {
			this.classList.add('animate', 'active')
		}, 1)

		this.addEventListener(
			'transitionend',
			() => {
				const containerToTrapFocusOn = this.hasAttribute(ATTRIBUTES.cartEmpty)
					? q.rs(CartDrawer.selectors.innerEmpty, this)
					: q.rs(CartDrawer.selectors.container)
				const focusElement =
					q.os(CartDrawer.selectors.inner, this) || q.rs(CartDrawer.selectors.closeButton, this)
				window.TsDOM.trapFocus(containerToTrapFocusOn, focusElement)
			},
			{ once: true }
		)

		document.body.classList.add('overflow-hidden')
	}

	close() {
		this.classList.remove('active')
		window.TsDOM.removeTrapFocus(this.activeElement)
		document.body.classList.remove('overflow-hidden')
		this.clearCartHash();
	}

	setSummaryAccessibility(cartDrawerNote: HTMLElement) {
		const parentElement = cartDrawerNote.parentElement
		if (!parentElement) throw new Error('setSummaryAccessibility failed - No parent element found')
		cartDrawerNote.setAttribute('role', 'button')
		cartDrawerNote.setAttribute('aria-expanded', 'false')
		const nextElementSibling = cartDrawerNote.nextElementSibling

		if (nextElementSibling instanceof HTMLElement && nextElementSibling.hasAttribute('id')) {
			cartDrawerNote.setAttribute('aria-controls', nextElementSibling.id)
		}

		cartDrawerNote.addEventListener('click', (event: MouseEvent) => {
			const currentTarget = q.rct(event)
			const isExpanded = q.rs(CartDrawer.selectors.noteDetails, this).hasAttribute('open')
			currentTarget.setAttribute('aria-expanded', `${isExpanded}`)
		})

		parentElement.addEventListener('keyup', q.onKeyUpEscape)
	}

	renderContents(parsedState: SectionApiResponse) {
		q.os(CartDrawer.selectors.inner, this)?.hasAttribute(ATTRIBUTES.cartEmpty) &&
			q.rs(CartDrawer.selectors.inner, this).removeAttribute(ATTRIBUTES.cartEmpty)
		this.productId = parsedState.id
		this.getSectionsToRender().forEach((section) => {
			const sectionId = section.id
			if (!sectionId) throw new Error('Section id is required')


			const sectionElement = section.selector ? q.rs(section.selector) : q.rs(`#${sectionId}`)
			sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[sectionId], section.selector)
			if (sectionId === 'cart-icon-bubble') {
				const newCartHTML = this.getSectionInnerHTML(parsedState.sections[sectionId], section.selector)

				q.rl('[data-uc-cart-icon-bubble]').forEach(el => {
					el.innerHTML = newCartHTML
					const countEl = q.rs('[data-count]', el)
					const count = parseInt(q.ra(countEl, 'data-count'))
					console.log({count})
					const headerCartButton = q.rs('[data-uc-header-cart-button]')
					if (count === 0) {
						headerCartButton.classList.add('empty')
					} else {
						headerCartButton.classList.remove('empty')
					}
				})

			} else {
				const sectionElement = section.selector ? q.rs(section.selector) : q.rs(`#${sectionId}`)
				sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[sectionId], section.selector)
			}
		})

		setTimeout(() => {
			this.getOverlay().addEventListener('click', this.close.bind(this))
			this.open()
		})
	}

	getSectionInnerHTML(html: string, selector = '.shopify-section') {
		const newDocument = new DOMParser().parseFromString(html, 'text/html')
		return q.rs(selector, newDocument.documentElement).innerHTML
	}

	getSectionsToRender(): ShopifySectionRenderingSchema[] {
		return [
			{
				id: 'cart-drawer',
				selector: CartDrawer.selectors.container,
			},
			{
				id: 'cart-icon-bubble',
				selector: SELECTORS.cartLink,
			},
		]
	}

	getSectionDOM(html: string, selector = '.shopify-section') {
		return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)
	}

	setActiveElement(element: HTMLElement) {
		this.activeElement = element
	}
}
